import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { io } from "socket.io-client";

const TABS = ["セッション管理", "チャット", "生徒一覧", "モニタリング"];

export default function TeacherPage() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState({});
  const [students, setStudents] = useState([]);
  const [editStudent, setEditStudent] = useState(null);
  const [editPw, setEditPw] = useState("");
  const [instruction, setInstruction] = useState("");
  const [introSent, setIntroSent] = useState(false);
  const [introMsg, setIntroMsg] = useState("");
  const [msg, setMsg] = useState("");
  const [bulkCount, setBulkCount] = useState(3);
  const [chatInput, setChatInput] = useState("");
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  function handleAuth(r) {
    if (r.status === 401) { logout(); navigate("/login"); return null; }
    return r.json();
  }

  function loadSessions() {
    fetch("/api/sessions", { headers: { Authorization: "Bearer " + token } })
      .then(handleAuth)
      .then(d => { if (d) setSessions(Array.isArray(d) ? d : []); })
      .catch(console.error);
  }

  useEffect(() => {
    loadSessions();
    fetch("/api/students", { headers: { Authorization: "Bearer " + token } })
      .then(handleAuth)
      .then(d => { if (d) setStudents(Array.isArray(d) ? d : []); })
      .catch(console.error);
  }, [token]);

  useEffect(() => {
    const socket = io({ auth: { token } });
    socketRef.current = socket;
    socket.emit("auth", token);
    socket.on("auth_error", () => { logout(); navigate("/login"); });
    socket.on("message", msg => {
      setMessages(prev => [...prev, msg]);
      setAllMessages(prev => ({
        ...prev,
        [msg.session_id]: [...(prev[msg.session_id] || []), msg]
      }));
    });
    socket.on("history", msgs => {
      setMessages(msgs);
      if (msgs.length > 0) {
        setAllMessages(prev => ({ ...prev, [msgs[0].session_id]: msgs }));
      }
    });
    socket.on("session_status", ({ status }) => {
      setCurrentSession(prev => prev ? { ...prev, status } : prev);
    });
    return () => socket.disconnect();
  }, [token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  function joinSession(session) {
    setCurrentSession(session);
    setIntroSent(false);
    setIntroMsg("");
    socketRef.current?.emit("join_session", session.id);
    setTab(1);
  }

  async function createSession(e) {
    e.preventDefault();
    const fd = new FormData(e.target);
    const body = {
      target_level: fd.get("target_level"),
      situation: fd.get("situation"),
      activity_time: Number(fd.get("activity_time")),
      goal: fd.get("goal")
    };
    const r = await fetch("/api/sessions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify(body)
    });
    const session = await r.json();
    setSessions(prev => [session, ...prev]);
    joinSession(session);
    e.target.reset();
  }

  async function createBulkSessions(e) {
    e.preventDefault();
    const fd = new FormData(e.target);
    const body = {
      target_level: fd.get("target_level"),
      situation: fd.get("situation"),
      activity_time: Number(fd.get("activity_time")),
      goal: fd.get("goal"),
      count: Number(bulkCount)
    };
    const r = await fetch("/api/sessions/bulk", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify(body)
    });
    const newSessions = await r.json();
    setSessions(prev => [...newSessions, ...prev]);
    setMsg(bulkCount + "つのグループを作成しました");
    e.target.reset();
  }

  async function updateStatus(status) {
    if (!currentSession) return;
    await fetch("/api/sessions/" + currentSession.id + "/status", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ status })
    });
    setCurrentSession(prev => ({ ...prev, status }));
    setSessions(prev => prev.map(s => s.id === currentSession.id ? { ...s, status } : s));
  }

  async function updateAllStatus(status) {
    for (const s of sessions.filter(s => s.status === "active" || s.status === "waiting")) {
      await fetch("/api/sessions/" + s.id + "/status", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ status })
      });
    }
    loadSessions();
  }

  async function sendInstruction(e) {
    e.preventDefault();
    if (!instruction.trim() || !currentSession) return;
    if (currentSession.status === "waiting") await updateStatus("active");
    const r = await fetch("/api/sessions/" + currentSession.id + "/fasili-intro", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ instruction: instruction.trim() })
    });
    if (r.ok) {
      setIntroSent(true);
      setIntroMsg("ファシリさんに指令を送りました。あとは生徒たちに任せましょう！");
    }
  }

  async function downloadLog(sessionId) {
    const r = await fetch("/api/sessions/" + sessionId + "/download", {
      headers: { Authorization: "Bearer " + token }
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "chat_log.csv";
    a.click();
    URL.revokeObjectURL(url);
    if (window.confirm("ダウンロードが完了しました。このセッションのデータを削除しますか？")) {
      await deleteSession(sessionId);
    }
  }

  async function deleteSession(sessionId) {
    await fetch("/api/sessions/" + sessionId, {
      method: "DELETE",
      headers: { Authorization: "Bearer " + token }
    });
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    if (currentSession?.id === sessionId) setCurrentSession(null);
    setMsg("セッションを削除しました");
  }

  async function resetChat() {
    if (!currentSession) return;
    if (!window.confirm("チャットをリセットしますか？メッセージが全て削除されます。")) return;
    await fetch("/api/sessions/" + currentSession.id + "/messages", {
      method: "DELETE",
      headers: { Authorization: "Bearer " + token }
    });
    setMessages([]);
    setIntroSent(false);
    setInstruction("");
    setIntroMsg("");
  }

  async function updateStudentPw(studentId) {
    if (!editPw.trim()) return;
    try {
      const r = await fetch("/api/admin/students/" + studentId, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ password: editPw })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setMsg("パスワードを更新しました");
      setEditStudent(null);
      setEditPw("");
    } catch(err) { setMsg("エラー：" + err.message); }
  }

  function joinMonitor(session) {
    // モニタリングの「参加」→ そのセッションのチャットに教師として参加（発言可）
    joinSession(session);
  }

  function sendTeacherMsg(e) {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || !currentSession) return;
    socketRef.current?.emit("message", { sessionId: currentSession.id, content: text });
    setChatInput("");
  }

  function togglePause() {
    if (!currentSession) return;
    updateStatus(currentSession.status === "paused" ? "active" : "paused");
  }

  function getRoleBadge(msg) {
    if (msg.sender_type === "bot") {
      const isEvil = msg.sender_name && msg.sender_name.includes("トラブル");
      return { bg: isEvil ? "#7f1d1d" : "#1e3a5f", label: (isEvil ? "😈 " : "🎓 ") + msg.sender_name };
    }
    if (msg.sender_type === "teacher") return { bg: "#0f3460", label: "先生" };
    return { bg: "#1a3a1a", label: msg.sender_name };
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.sidebar}>
        <div style={styles.sideHead}>
          <div style={styles.sideTitle}>SNSまなびチャット</div>
          <div style={styles.sideSub}>先生 · {user?.display_name}</div>
        </div>
        <div style={styles.sideSection}>セッション</div>
        {sessions.map(s => (
          <div key={s.id} style={{...styles.sideItem, ...(currentSession?.id===s.id ? styles.sideItemActive : {})}}
            onClick={() => joinSession(s)}>
            <div style={styles.sideItemName}>{s.situation}</div>
            <div style={styles.sideItemSub}>
              {s.status === "active" ? "🟢進行中" : s.status === "paused" ? "⏸一時停止中" : s.status === "ended" ? "⚫終了" : "待機中"}
              {s.session_code && <span style={styles.codeTag}>{s.session_code}</span>}
            </div>
          </div>
        ))}
        <div style={styles.sideFooter}>
          <button style={styles.logoutBtn} onClick={() => { logout(); navigate("/login"); }}>ログアウト</button>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.mainHead}>
          <div style={styles.tabs}>
            {TABS.map((t, i) => (
              <button key={i} style={{...styles.tab, ...(tab===i ? styles.tabActive : {})}} onClick={() => setTab(i)}>{t}</button>
            ))}
          </div>
          {currentSession && tab === 1 && (
            <div style={styles.sessionBadge}>
              <span style={{fontWeight:600}}>{currentSession.situation}</span>
              <span style={styles.codeTag}>{currentSession.session_code}</span>
              {currentSession.status === "active" ? (
                <button style={styles.btnDanger} onClick={() => updateStatus("ended")}>終了・まとめ</button>
              ) : currentSession.status === "ended" ? (
                <span style={{color:"var(--text-muted)", fontSize:12}}>終了済み</span>
              ) : (
                <button style={styles.btnSuccess} onClick={() => updateStatus("active")}>開始</button>
              )}
            </div>
          )}
        </div>

        {tab === 0 && (
          <div style={styles.content}>
            {msg && <div style={styles.successMsg}>{msg}</div>}
            <div style={styles.twoCol}>
              <div>
                <h2 style={styles.h2}>1グループ作成</h2>
                <form onSubmit={createSession} style={styles.form}>
                  <select name="target_level" style={styles.input} required>
                    <option value="">対象を選択</option>
                    <option>小学校</option>
                    <option>中学校</option>
                    <option>高校段階</option>
                    <option>特別支援学校</option>
                    <option>大学生・専門学校生</option>
                    <option>社会人</option>
                  </select>
                  <input name="situation" style={styles.input} placeholder="状況設定" required />
                  <input name="activity_time" type="number" style={styles.input} placeholder="活動時間（分）" required />
                  <textarea name="goal" style={{...styles.input, height:60}} placeholder="ねらい（任意）" />
                  <button style={styles.btnPrimary}>作成してチャットへ</button>
                </form>
              </div>
              <div>
                <h2 style={styles.h2}>複数グループ一括作成</h2>
                <form onSubmit={createBulkSessions} style={styles.form}>
                  <select name="target_level" style={styles.input} required>
                    <option value="">対象を選択</option>
                    <option>小学校</option>
                    <option>中学校</option>
                    <option>高校段階</option>
                    <option>特別支援学校</option>
                    <option>大学生・専門学校生</option>
                    <option>社会人</option>
                  </select>
                  <input name="situation" style={styles.input} placeholder="状況設定（グループ番号が自動追加）" required />
                  <input name="activity_time" type="number" style={styles.input} placeholder="活動時間（分）" required />
                  <textarea name="goal" style={{...styles.input, height:60}} placeholder="ねらい（任意）" />
                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                    <label style={{color:"var(--text-muted)", fontSize:13}}>グループ数：</label>
                    <input type="number" min="2" max="10" style={{...styles.input, width:80}} value={bulkCount} onChange={e => setBulkCount(e.target.value)} />
                  </div>
                  <button style={styles.btnSecondary}>一括作成</button>
                </form>
              </div>
            </div>

            <h2 style={{...styles.h2, marginTop:24}}>セッション一覧</h2>
            <div style={{display:"flex", gap:8, marginBottom:12}}>
              <button style={styles.btnSuccess} onClick={() => updateAllStatus("active")}>全て開始</button>
              <button style={styles.btnDanger} onClick={() => updateAllStatus("ended")}>全て終了</button>
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>コード</th>
                  <th style={styles.th}>状況設定</th>
                  <th style={styles.th}>対象</th>
                  <th style={styles.th}>状態</th>
                  <th style={styles.th}>操作</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map(s => (
                  <tr key={s.id}>
                    <td style={{...styles.td, fontWeight:700, color:"var(--accent2)", letterSpacing:2}}>{s.session_code}</td>
                    <td style={styles.td}>{s.situation}</td>
                    <td style={styles.td}>{s.target_level}</td>
                    <td style={styles.td}>{s.status === "active" ? "🟢進行中" : s.status === "paused" ? "⏸一時停止中" : s.status === "ended" ? "⚫終了" : "待機中"}</td>
                    <td style={styles.td}>
                      <div style={{display:"flex", gap:4}}>
                        <button style={styles.btnSmall} onClick={() => joinSession(s)}>開く</button>
                        {s.status === "ended" && (
                          <button style={styles.btnSmall} onClick={() => downloadLog(s.id)}>DL</button>
                        )}
                        <button style={{...styles.btnSmall, color:"var(--danger)"}} onClick={() => { if(window.confirm("削除しますか？")) deleteSession(s.id); }}>削除</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 1 && (
          <div style={styles.chatWrap}>
            {!currentSession ? (
              <div style={styles.empty}>左のサイドバーからセッションを選んでください</div>
            ) : (
              <>
                {currentSession.status !== "ended" && (
                  <div style={styles.instructionArea}>
                    {!introSent ? (
                      <>
                        <div style={styles.instructionTitle}>ファシリさんへの指令</div>
                        <form onSubmit={sendInstruction} style={styles.instructionForm}>
                          <textarea style={styles.instructionInput} value={instruction}
                            onChange={e => setInstruction(e.target.value)}
                            placeholder="例：今日は小学生のグループLINEでのトラブルを体験してもらいます。" rows={2} />
                          <div style={{display:"flex", gap:8}}>
                          <button style={styles.btnFasili} disabled={!instruction.trim()}>指令を送る・開始</button>
                          <button type="button" style={styles.btnReset} onClick={resetChat}>リセット</button>
                        </div>
                        </form>
                      </>
                    ) : (
                      <div style={styles.introSentMsg}>
                        {introMsg}
                        <button style={styles.btnSmall} onClick={() => setIntroSent(false)}>再送信</button>
                        <button style={styles.btnReset} onClick={resetChat}>リセット</button>
                      </div>
                    )}
                  </div>
                )}
                {currentSession.status !== "ended" && (
                  <div style={{display:"flex", alignItems:"center", gap:10, padding:"8px 0"}}>
                    <button style={styles.btnSmall} onClick={togglePause}>
                      {currentSession.status === "paused" ? "▶ 再開する" : "⏸ 一時停止"}
                    </button>
                    {currentSession.status === "paused" &&
                      <span style={{color:"#f59e0b", fontSize:13, fontWeight:600}}>一時停止中（生徒は発言できません）</span>}
                  </div>
                )}
                <div style={styles.messages}>
                  {messages.map((msg, i) => {
                    const badge = getRoleBadge(msg);
                    return (
                      <div key={i} style={styles.msgWrap}>
                        <div style={{...styles.msgBadge, background: badge.bg}}>{badge.label}</div>
                        <div style={styles.msgContent}>{msg.content}</div>
                        <div style={styles.msgTime}>{new Date(msg.created_at).toLocaleTimeString("ja-JP", {hour:"2-digit",minute:"2-digit"})}</div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                {currentSession.status !== "ended" && (
                  <form onSubmit={sendTeacherMsg} style={{display:"flex", gap:8, marginTop:8}}>
                    <input style={{...styles.input, flex:1}} value={chatInput}
                      onChange={e => setChatInput(e.target.value)}
                      placeholder="先生として発言（生徒の画面にも表示されます）" />
                    <button style={styles.btnPrimary} disabled={!chatInput.trim()}>送信</button>
                  </form>
                )}
                {currentSession.status === "ended" && (
                  <div style={styles.endedBar}>
                    セッション終了
                    <button style={{...styles.btnSmall, marginLeft:8}} onClick={() => downloadLog(currentSession.id)}>ログをダウンロード</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {tab === 2 && (
          <div style={styles.content}>
            <h2 style={styles.h2}>生徒一覧（{students.length}人）</h2>
            {msg && <div style={styles.successMsg}>{msg}</div>}
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>学年</th>
                  <th style={styles.th}>組</th>
                  <th style={styles.th}>番号</th>
                  <th style={styles.th}>操作</th>
                </tr>
              </thead>
              <tbody>
                {students.map(s => (
                  <tr key={s.id}>
                    <td style={styles.td}>{s.grade}年</td>
                    <td style={styles.td}>{s.class_name}</td>
                    <td style={styles.td}>{s.student_number}番</td>
                    <td style={styles.td}>
                      {editStudent?.id === s.id ? (
                        <div style={{display:"flex", gap:4, alignItems:"center"}}>
                          <input type="text" style={{...styles.input, width:120, padding:"4px 8px"}}
                            placeholder="新PW" value={editPw} onChange={e => setEditPw(e.target.value)} />
                          <button style={styles.btnSmall} onClick={() => updateStudentPw(s.id)}>更新</button>
                          <button style={styles.btnSmall} onClick={() => { setEditStudent(null); setEditPw(""); }}>取消</button>
                        </div>
                      ) : (
                        <button style={styles.btnSmall} onClick={() => setEditStudent(s)}>PW変更</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 3 && (
          <div style={styles.monitorWrap}>
            <div style={styles.monitorHead}>
              <h2 style={styles.h2}>モニタリング</h2>
              <div style={{display:"flex", gap:8}}>
                <button style={styles.btnSuccess} onClick={() => updateAllStatus("active")}>全て開始</button>
                <button style={styles.btnDanger} onClick={() => updateAllStatus("ended")}>全て終了・まとめ</button>
              </div>
            </div>
            <div style={styles.monitorGrid}>
              {sessions.filter(s => s.status !== "ended").map(s => {
                const msgs = allMessages[s.id] || [];
                const hasNew = msgs.length > 0;
                return (
                  <div key={s.id} style={{...styles.monitorCard, border: hasNew ? "1px solid var(--accent2)" : "1px solid var(--border)"}}>
                    <div style={styles.monitorCardHead}>
                      <span style={styles.monitorCode}>{s.session_code}</span>
                      <span style={styles.monitorSituation}>{s.situation}</span>
                      <span style={s.status === "active" ? styles.statusActive : styles.statusWaiting}>
                        {s.status === "active" ? "進行中" : s.status === "paused" ? "一時停止中" : "待機中"}
                      </span>
                      <button style={styles.btnSmall} onClick={() => { joinMonitor(s); }}>参加</button>
                    </div>
                    <div style={styles.monitorMessages}>
                      {msgs.slice(-5).map((msg, i) => {
                        const isEvil = msg.sender_name && msg.sender_name.includes("トラブル");
                        return (
                          <div key={i} style={{...styles.monitorMsg, color: isEvil ? "#fca5a5" : msg.sender_type === "bot" ? "#93c5fd" : "var(--text)"}}>
                            <span style={styles.monitorSender}>{msg.sender_name}:</span>
                            <span>{msg.content.length > 30 ? msg.content.slice(0,30) + "..." : msg.content}</span>
                          </div>
                        );
                      })}
                      {msgs.length === 0 && <div style={{color:"var(--text-muted)", fontSize:12}}>メッセージなし</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: { display: "flex", height: "100vh", overflow: "hidden", background: "var(--bg-dark)" },
  sidebar: { width: 240, background: "#0d0d1a", display: "flex", flexDirection: "column", flexShrink: 0, height: "100vh", overflowY: "auto" },
  sideHead: { padding: "16px", borderBottom: "1px solid var(--border)" },
  sideTitle: { color: "#fff", fontWeight: 700, fontSize: 14 },
  sideSub: { color: "var(--text-muted)", fontSize: 11, marginTop: 2 },
  sideSection: { color: "var(--text-muted)", fontSize: 11, padding: "12px 16px 4px", textTransform: "uppercase", letterSpacing: 1 },
  sideItem: { padding: "8px 16px", cursor: "pointer", borderRadius: 6, margin: "2px 8px" },
  sideItemActive: { background: "var(--bg-light)" },
  sideItemName: { color: "var(--text)", fontSize: 13, fontWeight: 600 },
  sideItemSub: { color: "var(--text-muted)", fontSize: 11, marginTop: 2, display:"flex", alignItems:"center", gap:4 },
  codeTag: { background: "var(--bg-light)", color: "var(--accent2)", fontSize: 10, padding: "1px 6px", borderRadius: 4, fontWeight: 700, letterSpacing: 1 },
  sideFooter: { marginTop: "auto", padding: 16, borderTop: "1px solid var(--border)" },
  logoutBtn: { width: "100%", padding: "8px", borderRadius: 6, background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)", fontSize: 12 },
  main: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  mainHead: { padding: "12px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
  tabs: { display: "flex", gap: 4 },
  tab: { padding: "6px 14px", borderRadius: 6, background: "transparent", color: "var(--text-muted)", fontSize: 13, cursor:"pointer", border:"none" },
  tabActive: { background: "var(--bg-light)", color: "var(--text)" },
  sessionBadge: { display: "flex", alignItems: "center", gap: 8, color: "var(--text-muted)", fontSize: 13 },
  content: { flex: 1, overflowY: "auto", padding: 24 },
  twoCol: { display: "flex", gap: 32, flexWrap: "wrap" },
  h2: { fontSize: 16, fontWeight: 700, marginBottom: 12, color: "var(--text)" },
  form: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 360 },
  input: { padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--bg-mid)", color: "var(--text)", fontSize: 13, width: "100%" },
  btnPrimary: { padding: "10px 16px", borderRadius: 8, background: "var(--accent)", color: "#000", fontWeight: 700, fontSize: 13, cursor:"pointer", border:"none" },
  btnSecondary: { padding: "10px 16px", borderRadius: 8, background: "var(--accent2)", color: "#fff", fontWeight: 700, fontSize: 13, cursor:"pointer", border:"none" },
  btnSuccess: { padding: "4px 12px", borderRadius: 6, background: "var(--accent)", color: "#000", fontWeight: 700, fontSize: 12, cursor:"pointer", border:"none" },
  btnDanger: { padding: "4px 12px", borderRadius: 6, background: "var(--danger)", color: "#fff", fontWeight: 700, fontSize: 12, cursor:"pointer", border:"none" },
  btnFasili: { padding: "10px 20px", borderRadius: 8, background: "#7c3aed", color: "#fff", fontWeight: 700, fontSize: 14, cursor:"pointer", border:"none" },
  btnReset: { padding: "8px 16px", borderRadius: 8, background: "#374151", color: "#fff", fontWeight: 700, fontSize: 13, cursor:"pointer", border:"1px solid var(--border)" },
  btnSmall: { padding: "4px 10px", borderRadius: 6, background: "var(--bg-light)", border: "1px solid var(--border)", color: "var(--text-muted)", fontSize: 12, cursor: "pointer" },
  instructionArea: { padding: "12px 16px", borderBottom: "1px solid var(--border)", background: "rgba(124,58,237,0.1)" },
  instructionTitle: { color: "#a78bfa", fontSize: 13, fontWeight: 700, marginBottom: 8 },
  instructionForm: { display: "flex", flexDirection: "column", gap: 8 },
  instructionInput: { padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(124,58,237,0.4)", background: "var(--bg-dark)", color: "var(--text)", fontSize: 14, resize: "vertical" },
  introSentMsg: { color: "var(--accent)", fontSize: 13, display: "flex", alignItems: "center", gap: 8 },
  chatWrap: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  messages: { flex: 1, overflowY: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 8 },
  msgWrap: { display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" },
  msgBadge: { fontSize: 11, padding: "2px 8px", borderRadius: 12, color: "#fff", flexShrink: 0 },
  msgContent: { color: "var(--text)", fontSize: 14, flex: 1 },
  msgTime: { color: "var(--text-muted)", fontSize: 11, flexShrink: 0 },
  endedBar: { padding: "10px 16px", background: "rgba(248,113,113,0.1)", color: "var(--danger)", fontSize: 13, textAlign: "center", display:"flex", alignItems:"center", justifyContent:"center" },
  empty: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)", fontSize: 14 },
  table: { borderCollapse: "collapse", fontSize: 13, width: "100%" },
  th: { padding: "8px 12px", background: "var(--bg-light)", color: "var(--text-muted)", textAlign: "left", borderBottom: "1px solid var(--border)" },
  td: { padding: "8px 12px", color: "var(--text)", borderBottom: "1px solid var(--border)" },
  successMsg: { background: "rgba(74,222,128,0.1)", border: "1px solid var(--accent)", borderRadius: 8, padding: "8px 14px", color: "var(--accent)", fontSize: 13, marginBottom: 12 },
  monitorWrap: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", padding: 16 },
  monitorHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  monitorGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12, overflowY: "auto" },
  monitorCard: { background: "var(--bg-mid)", borderRadius: 10, padding: 12 },
  monitorCardHead: { display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" },
  monitorCode: { background: "var(--bg-light)", color: "var(--accent2)", fontSize: 12, padding: "2px 8px", borderRadius: 4, fontWeight: 700, letterSpacing: 1 },
  monitorSituation: { color: "var(--text)", fontSize: 12, fontWeight: 600, flex: 1 },
  statusActive: { fontSize: 11, color: "var(--accent)", background: "rgba(74,222,128,0.1)", padding: "2px 8px", borderRadius: 12 },
  statusWaiting: { fontSize: 11, color: "var(--text-muted)", background: "var(--bg-light)", padding: "2px 8px", borderRadius: 12 },
  monitorMessages: { display: "flex", flexDirection: "column", gap: 4 },
  monitorMsg: { fontSize: 12, display: "flex", gap: 4 },
  monitorSender: { fontWeight: 700, flexShrink: 0 },
};
